/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import PropTypes from 'prop-types';
import { today } from '@internationalized/date';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';

import { useWindowSize } from 'sarsaparilla';
import { getPrice, isOffline } from '../../utils';
import { durationLength } from '../../utils/duration_utils';
import { getValidPriceFromDivisionV2, parseDate } from '../../utils/date_time_utils';

export default function PassTypes({ passTypes }) {
    const { width } = useWindowSize();

    if (!passTypes?.length) {
        return null;
    }

    const isFieldSales =
        passTypes?.length === 1 && passTypes?.[0]?.features?.is_field_sales;
    const passTypesFiltered = passTypes.filter((item) => {
        const itemIsFieldSales = !!item?.features?.is_field_sales;
        const hasValidStartDate = item.periods.some(
            (period) => today().compare(parseDate(period.start_date)) >= 0
        );
        return !itemIsFieldSales && hasValidStartDate;
    });
    const facilityIsOffline = isOffline(passTypesFiltered);
    const offlinePassTypesSorted = orderBy(passTypesFiltered, ['name'], ['asc']);
    const passTypesSorted = passTypesFiltered.sort((a, b) => {
        return (
            parseFloat(getPrice(b?.periods).replace(/\/.*|\D/g, ' ')) -
            parseFloat(getPrice(a?.periods).replace(/\/.*|\D/g, ' '))
        );
    });
    const getDuration = (durationRaw) => {
        let duration = durationRaw
            .replace(/-offline/g, '')
            .replace(/-/g, ' ')
            .replace(/day/g, 'Day')
            .replace(/month/g, 'Month');

        duration = duration.includes('semiannual')
            ? duration.replace(/semiannual/g, 'Semiannual')
            : duration.replace(/annual/g, 'Annual');

        if (parseInt(duration, 10) > 1) {
            return `${duration}s`;
        }

        return duration;
    };
    const isLessThanDesktop = width < 998;

    const getPriceFromPeriods = (passType) => {
        const price = getValidPriceFromDivisionV2(passType?.periods, today());
        return isEmpty(price) ? '0' : price;
    };

    if (isFieldSales) {
        return (
            <div className="ap-pass-types">
                <table>
                    <caption className="rec-sr-only">
                        Activity pass options purchase on site
                    </caption>
                    <thead>
                        <tr>
                            <th scope="col">Type</th>
                            <th scope="col" />
                        </tr>
                    </thead>
                    <tbody>
                        {passTypes?.map((passType) => {
                            return (
                                <tr key={passType?.id}>
                                    <td>{passType?.name}</td>
                                    <td>Must be purchased on site</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    if (facilityIsOffline) {
        return (
            <div className="ap-pass-types">
                <table>
                    <caption className="rec-sr-only">
                        Activity pass options facility is offline
                    </caption>
                    <thead>
                        <tr>
                            <th scope="col">Type</th>
                            <th scope="col">Duration</th>
                        </tr>
                    </thead>
                    <tbody>
                        {offlinePassTypesSorted?.map((passType) => {
                            return (
                                <tr key={passType?.id}>
                                    <td>{passType?.name}</td>
                                    <td>{getDuration(passType?.duration)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <div className="ap-pass-types">
            {!isLessThanDesktop && (
                <table>
                    <caption className="rec-sr-only">
                        Activity pass options with description
                    </caption>
                    <thead>
                        <tr>
                            <th scope="col">Type</th>
                            <th scope="col">Description</th>
                            <th scope="col">Duration</th>
                            <th scope="col">Price</th>
                        </tr>
                    </thead>
                    <tbody>
                        {passTypesSorted?.map((passType) => {
                            return (
                                <tr key={passType?.id}>
                                    <td>{passType?.name}</td>
                                    <td>{passType?.used_for}</td>
                                    <td>{durationLength(passType?.duration)}</td>
                                    <td>{getPriceFromPeriods(passType)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
            {isLessThanDesktop && (
                // eslint-disable-next-line react/jsx-no-useless-fragment
                <>
                    {passTypesSorted?.map((passType) => {
                        return (
                            <table key={passType.id} className="ap-pass-types-mobile">
                                <caption className="rec-sr-only">
                                    Activity pass options
                                </caption>
                                <thead>
                                    <tr>
                                        <th scope="col">{passType?.name}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        {!isEmpty(passType?.used_for) && (
                                            <td>{passType?.used_for}</td>
                                        )}
                                    </tr>
                                    <tr>
                                        <td>
                                            <strong>Duration:</strong>{' '}
                                            {durationLength(passType?.duration)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <strong>Price:</strong>{' '}
                                            {getPriceFromPeriods(passType)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        );
                    })}
                </>
            )}
        </div>
    );
}

PassTypes.propTypes = {
    passTypes: PropTypes.array.isRequired,
};
